import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { formatDistanceToNow, parseISO } from "date-fns";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export const NotificationBell = () => {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const lastUnread = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      if (lastUnread.current !== null && data.unread > lastUnread.current && data.items[0]) {
        toast(data.items[0].title, { description: data.items[0].body });
      }
      lastUnread.current = data.unread;
      setData(data);
    } catch (e) {
      // polling errors are non-fatal
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    window.addEventListener("onn:refresh-notifications", load);
    return () => {
      clearInterval(t);
      window.removeEventListener("onn:refresh-notifications", load);
    };
  }, [load]);

  const openItem = async (n) => {
    setOpen(false);
    if (!n.read) await api.post(`/notifications/${n.id}/read`);
    load();
    navigate(n.link || "/notifications");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" className="relative w-10 h-10 grid place-items-center rounded-full hover:bg-slate-100 transition-colors">
          <Bell className="w-5 h-5 text-slate-700" />
          {data.unread > 0 && (
            <span data-testid="notification-unread-count" className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold grid place-items-center">
              {data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[340px] p-0" data-testid="notification-popover">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <span className="font-bold text-[#24474A]">Notifications</span>
          <button className="text-xs text-slate-500 hover:text-[#24474A]" data-testid="notification-popover-view-all" onClick={() => { setOpen(false); navigate("/notifications"); }}>
            View all
          </button>
        </div>
        <div className="max-h-[360px] overflow-y-auto">
          {data.items.slice(0, 6).map((n) => (
            <button key={n.id} onClick={() => openItem(n)} data-testid={`notification-popover-item-${n.id}`} className="w-full text-left px-4 py-3 border-b last:border-0 hover:bg-slate-50 flex gap-3">
              <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${n.read ? "bg-transparent" : "bg-[#24474A]"}`} />
              <span>
                <span className="block text-sm font-bold text-slate-800">{n.title}</span>
                <span className="block text-xs text-slate-600 line-clamp-2">{n.body}</span>
                <span className="block text-[11px] text-slate-400 mt-1">{formatDistanceToNow(parseISO(n.created_at), { addSuffix: true })}</span>
              </span>
            </button>
          ))}
          {data.items.length === 0 && <div className="px-4 py-8 text-center text-sm text-slate-500">You are all caught up.</div>}
        </div>
      </PopoverContent>
    </Popover>
  );
};
